<script lang="ts" setup>
import { computed, ref } from 'vue'

import {
  choosePickMethod,
  detectPickCapabilities,
  type PickCapabilities,
  type PickMethod
} from '@/utils/file-pick-ladder'
import { filesFromDirectoryHandle, pickDirectory } from '@/utils/vendors/tesla-folder'
import { formatNumber } from '@/utils/format'
import TeslaCopyToFilesGuide from './TeslaCopyToFilesGuide.vue'

// The one way to get Tesla files into the page. It walks the pick ladder (see utils/file-pick-ladder.ts)
// and steps down on its own when a method fails. Nothing is read or uploaded here: it only emits what
// the user picked.

export interface PickedFiles {
  files: File[]
  name: string
  mode: 'folder' | 'files'
}

const props = defineProps<{ capabilities?: PickCapabilities; disabled?: boolean }>()
const emit = defineEmits<{ picked: [PickedFiles] }>()

const caps = props.capabilities ?? detectPickCapabilities()
const failed = ref<PickMethod[]>([])
const preferFiles = ref(false)
const notice = ref('')

const ladderMethod = computed(() => choosePickMethod(caps, failed.value))
const isFolderRung = computed(
  () => ladderMethod.value === 'directory-picker' || ladderMethod.value === 'directory-input'
)
// "Pick an event's files instead" only changes what the button does; it never marks a rung failed.
const method = computed<PickMethod>(() => {
  if (preferFiles.value && caps.multiSelect) return 'multi-select'
  return ladderMethod.value
})
const pickingFolder = computed(
  () => method.value === 'directory-picker' || method.value === 'directory-input'
)
const canSwitchToFiles = computed(() => isFolderRung.value && caps.multiSelect)

const guideOpen = ref(ladderMethod.value === 'guided')

const folderInputRef = ref<HTMLInputElement | null>(null)
const filesInputRef = ref<HTMLInputElement | null>(null)

function hasRelativePath(file: File): boolean {
  return !!(file as File & { webkitRelativePath?: string }).webkitRelativePath
}

function filesName(files: File[]): string {
  return `${formatNumber(files.length)} ${files.length === 1 ? 'file' : 'files'}`
}

async function pickWithDirectoryPicker() {
  try {
    const handle = await pickDirectory()
    const files = await filesFromDirectoryHandle(handle)
    emit('picked', { files, name: handle.name, mode: 'folder' })
  } catch (err) {
    if ((err as DOMException)?.name === 'AbortError') return
    console.error('Directory picker failed', err)
    failed.value = [...failed.value, 'directory-picker']
    notice.value = "That didn't work in this browser. Tap again to try another way."
  }
}

async function choose() {
  if (props.disabled) return
  notice.value = ''
  if (method.value === 'directory-picker') return pickWithDirectoryPicker()
  if (method.value === 'directory-input') return folderInputRef.value?.click()
  filesInputRef.value?.click()
}

function takeFiles(e: Event): File[] {
  const input = e.target as HTMLInputElement
  const files = Array.from(input.files ?? [])
  input.value = '' // Lets the same selection be picked again.
  return files
}

function onFolderInput(e: Event) {
  const files = takeFiles(e)
  if (files.length === 0) return
  if (files.some(hasRelativePath)) {
    const root = (files.find(hasRelativePath) as File).webkitRelativePath.split('/')[0]
    emit('picked', { files, name: root || 'Selected folder', mode: 'folder' })
    return
  }
  // This browser ignored webkitdirectory and gave a flat list: treat it as an event-files pick.
  failed.value = [...failed.value, 'directory-input']
  emit('picked', { files, name: filesName(files), mode: 'files' })
}

function onFilesInput(e: Event) {
  const files = takeFiles(e)
  if (files.length === 0) return
  emit('picked', { files, name: filesName(files), mode: 'files' })
}

function openGuide() {
  guideOpen.value = true
}

defineExpose({ openGuide })
</script>

<template>
  <div class="file-picker" data-testid="file-picker">
    <input
      v-if="caps.directoryInput"
      ref="folderInputRef"
      type="file"
      class="hidden"
      webkitdirectory
      multiple
      data-testid="file-pick-folder-input"
      @change="onFolderInput"
    />
    <input
      ref="filesInputRef"
      type="file"
      class="hidden"
      multiple
      data-testid="file-pick-files-input"
      @change="onFilesInput"
    />

    <q-btn
      v-if="method !== 'guided'"
      unelevated
      no-caps
      color="blue-8"
      class="full-width-mobile"
      :icon="pickingFolder ? 'mdi-folder-open' : 'mdi-file-multiple'"
      :label="pickingFolder ? 'Choose TeslaCam folder' : 'Choose event files'"
      :disable="disabled"
      data-testid="file-pick-button"
      @click="choose"
    />

    <div v-if="notice" class="text-body2 text-orange-9 q-mt-sm" data-testid="file-pick-notice">
      {{ notice }}
    </div>

    <div v-if="pickingFolder" class="text-caption text-blue-grey-7 q-mt-sm">
      Pick the <code>TeslaCam</code> folder at the root of your USB drive.
    </div>
    <div v-else-if="method === 'multi-select'" class="text-caption text-blue-grey-7 q-mt-sm">
      Open one event folder, e.g. SavedClips &gt; 2026-03-14_08-30-00, and select all its files,
      event.json too.
    </div>

    <div v-if="canSwitchToFiles" class="q-mt-xs">
      <q-btn
        flat
        dense
        no-caps
        size="sm"
        color="blue-8"
        :label="preferFiles ? 'Pick the TeslaCam folder instead' : `Pick an event's files instead`"
        data-testid="file-pick-use-files"
        @click="preferFiles = !preferFiles"
      />
    </div>

    <q-expansion-item
      v-model="guideOpen"
      dense
      label="Can't see your drive?"
      class="q-mt-sm"
      data-testid="file-pick-guide"
    >
      <div class="q-pa-sm">
        <tesla-copy-to-files-guide v-if="guideOpen" />
      </div>
    </q-expansion-item>
  </div>
</template>

<style scoped>
.hidden {
  display: none;
}

@media (max-width: 599px) {
  .full-width-mobile {
    width: 100%;
  }
}
</style>
